import { CornerDownLeftIcon, Loader2Icon, XIcon } from 'lucide-react'
import { useRef, useState } from 'react'

import { Button, Checkbox, EmailInput, Modal, Select } from 'slab'
import { cn } from 'utils'

import { type StagedInvite } from './ShareQuoteButtonModal'

export function ShareQuoteForm (
  {
    isSendingInvites,
    stagedInvites,
    onStagedInvitesChanged,
    onSendStagedInvites,
    onCancelClick,
    canSendEditorInvites,
    canSendOnboardInvites,
  }:
  {
    isSendingInvites: boolean,
    stagedInvites: StagedInvite[],
    onStagedInvitesChanged: (invites: StagedInvite[]) => void,
    onSendStagedInvites: () => void,
    onCancelClick: () => void,
    canSendEditorInvites: boolean,
    canSendOnboardInvites: boolean,
  }
) {
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const formRef = useRef<HTMLFormElement>(null)
  const emailInputRef = useRef<HTMLInputElement>(null)

  const removeInvite = (email: string) => {
    onStagedInvitesChanged(stagedInvites.filter(invite => invite.email !== email))
  }

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const form = e.target as HTMLFormElement
    const formData = new FormData(form)
    const email = formData.get('email') as string
    const isEditor = formData.get('ability') === 'editor'
    const canOnboard = formData.get('can_onboard') === 'true'

    const abilities: ('view' | 'edit' | 'onboard')[] = ['view']
    if (isEditor) abilities.push('edit')
    if (canOnboard) abilities.push('onboard')

    onStagedInvitesChanged([...stagedInvites, { email, abilities }])
    setErrorMessage(null)

    form.reset()
  }

  const attemptToSendStagedInvites = () => {
    if (emailInputRef.current && emailInputRef.current.value.trim().length > 0) {
      setErrorMessage('Unsubmitted email address, please complete the form.')
      return
    }
    onSendStagedInvites()
  }

  return (
    <>
      <form ref={formRef} onSubmit={onSubmit}>
        <div className='flex items-center gap-x-2 w-full'>
          <div className='flex-1'>
            <EmailInput
              placeholder='Email'
              name='email'
              disabled={isSendingInvites}
              ref={emailInputRef}
              required
              autoFocus
            />
          </div>
          {canSendEditorInvites && (
            <Select name='ability' defaultValue='viewer'>
              <option value='viewer'>Viewer</option>
              <option value='editor'>Editor</option>
            </Select>
          )}
          <Button
            type='submit'
            variant='tertiary'
            disabled={isSendingInvites}
            leftIcon={CornerDownLeftIcon}
          >
            Add
          </Button>
        </div>
        {errorMessage && <p className='text-red-500 text-sm mt-2'>{errorMessage}</p>}
        {canSendOnboardInvites && (
          <div className='mt-2'>
            <Checkbox
              label='Can onboard'
              description='Allows the individual to sign up their team with the specified allowance strategy'
              name='can_onboard'
              value='true'
            />
          </div>
        )}
      </form>
      <div
        className={cn(
          'overflow-hidden transition-all duration-300',
          stagedInvites.length === 0 ? 'max-h-0 opacity-0 pointer-events-none' : 'max-h-32 opacity-100 mt-2 pt-4 pb-2 border-t border-gray-200 flex flex-wrap items-start gap-2'
        )}
        aria-hidden={stagedInvites.length === 0}
      >
        {stagedInvites.map((invite, index) => (
          <div
            key={index}
            className='border border-gray-200 bg-gray-50 rounded-full px-2 py-1 text-sm space-x-2 whitespace-nowrap'
          >
            <span className='text-gray-800'>{invite.email}</span>
            <span className='text-gray-500'>
              {invite.abilities.includes('edit') ? 'Editor' : 'Viewer'}
              {invite.abilities.includes('onboard') && ', Can onboard'}
            </span>
            <button onClick={() => removeInvite(invite.email)} className='group'>
              <XIcon className='w-3 h-3 text-gray-500 group-hover:text-gray-800' />
            </button>
          </div>
        ))}
      </div>
      <Modal.Footer>
        <div className='flex items-center justify-end gap-x-2'>
          <Button
            variant='secondary'
            onClick={() => {
              formRef.current?.reset()
              onCancelClick()
            }}
            size='small'
          >
            Cancel
          </Button>
          <Button
            variant='primary'
            onClick={attemptToSendStagedInvites}
            size='small'
            disabled={isSendingInvites || stagedInvites.length === 0}
          >
            <div className='flex items-center gap-x-2'>
              Share with {stagedInvites.length} {stagedInvites.length === 1 ? 'person' : 'people'}
              {isSendingInvites && <Loader2Icon className='w-4 h-4 animate-spin' />}
            </div>
          </Button>
        </div>
      </Modal.Footer>
    </>
  )
}
